import { difference, filter } from "lodash";
import React from "react";
import ProductCompareData from "../projects/scanners/data/pages/ProductCompare";
import { collectTableData } from "./Tables";

// ?spe=3&something=else -> ?something=else
export function excludeFromSearch(urlParams, key) {
  const { history } = urlParams;
  const params = new URLSearchParams(history.location.search);
  const paramEntries = params.entries();
  let newParamsStr = "?";
  for (const entry of paramEntries) {
    if (newParamsStr.length > 1) {
      newParamsStr = `${newParamsStr}&`;
    }
    const [paramKey, paramValue] = entry;
    if (paramKey !== key) {
      newParamsStr = `${newParamsStr}${paramKey}=${paramValue}`;
    }
  }
  return newParamsStr;
}

export function localizePromos(index_page, selected_locale) {
  let local_promo_items = [];
  index_page.promos
    .filter((promo) => promo)
    .slice(0, 3)
    .forEach((promo) => {
      const local_body_disclaimer = promo?.getLocalized?.(
        "body_disclaimer",
        selected_locale
      );
      local_promo_items.push({
        heading: promo?.getLocalized?.("heading", selected_locale),
        body: promo?.getLocalized?.("body", selected_locale),
        body_disclaimer: local_body_disclaimer?.id,
        image_url: promo?.getLocalized?.("image", selected_locale),
        cta_label: promo?.getLocalized?.("link_text", selected_locale),
        cta_url: promo?.getLocalizedHref?.(selected_locale)
      });
    });
  return local_promo_items;
}

export function localizeNavigationItems(
  navigation_items,
  index_page,
  selected_locale
) {
  for (let i = 0; i < navigation_items.length; i++) {
    navigation_items[i].heading = index_page.getLocalized(
      `nav_${i + 1}_heading`,
      selected_locale
    );
    navigation_items[i].subheading = index_page.getLocalized(
      `nav_${i + 1}_body`,
      selected_locale
    );
    const nav_image = index_page.getLocalized(
      `nav_${i + 1}_image_url`,
      selected_locale
    );
    navigation_items[i].image_url = nav_image;
    navigation_items[i].cta_label = index_page.getLocalized(
      `nav_${i + 1}_cta_label`,
      selected_locale
    );
    if (i === 1) {
      navigation_items[i].brand_models_info.brand_dropdown_placeholder_text =
        index_page.getLocalized(
          `brand_dropdown_placeholder_text`,
          selected_locale
        );
      navigation_items[i].brand_models_info.model_dropdown_placeholder_text =
        index_page.getLocalized(
          `model_dropdown_placeholder_text`,
          selected_locale
        );
    }
  }
  return navigation_items;
}

export function localizeProductsByType(
  find_by_features,
  selected_locale,
  project_type,
  obj
) {
  const local_products = find_by_features.selected_types.map(
    (product_type) => ({
      type: product_type.getLocalized("label", selected_locale),
      products: filter(find_by_features.selected_products, {
        product_type: { name: product_type.name }
      }).map((product) => ({
        // label: `${product.family} ${product.model}`
        product_type: product.product_type,
        family: product.family,
        model_with_variant: `${product.model} ${product.variant || ""}`
          // Replace multiple spaces with single space, and trim excess
          .replace(/ +/g, " ")
          .trim(),

        image_url: product.getLocalized("product_image", selected_locale),
        // (product_image_proportion_thumb_key),
        image_alt: product.getLocalized("product_image", selected_locale)
          ?.title,
        href: product.href || `#${obj.html_id}`,

        // Simplify product feature objects
        features: product
          .getLocalized("attributes", selected_locale, [])
          .reduce((acc, next_feature) => {
            let value = "";
            const input_type = next_feature.attribute?.input_type;
            if (!input_type) return acc;
            switch (input_type) {
              case "text-input":
                value = next_feature.attribute_value;
                break;
              case "currency-display":
                value = next_feature.currency_value;
                break;
              case "numeric":
                value = next_feature.attribute_numeric_value;
                break;
              case "dropdown":
                value = next_feature.choice;
                break;
              case "checkboxes":
                value = next_feature.choices;
                break;
              default:
                value = next_feature.attribute_value;
                break;
            }

            // Stringify values for comparison
            if (Array.isArray(value)) {
              value = value.map((v) => `${v}`);
            } else {
              value = `${value}`;
            }

            return [
              ...acc,
              ["latin-bij", "bij"].indexOf(project_type) > -1
                ? {
                    feature_id: next_feature.attribute?.id,
                    value: value
                  }
                : {
                    attribute: next_feature.attribute,
                    facet: next_feature.facet,
                    feature_id: next_feature.attribute?.id,
                    value: value,
                    attribute_numeric_value_raw:
                      next_feature.attribute_numeric_value_raw,
                    display_value: next_feature.display_value
                  }
            ];
          }, [])
      }))
    })
  );
  return local_products;
}

export function localizeProductFeatures(
  collections,
  maps,
  language,
  attributes,
  prices,
  other_props
) {
  const feature_key = "attributes";
  let sorted_features = [];
  collections.attributes.forEach((feature) => {
    let found_product_features = attributes.filter((attribute) => {
      return (
        attribute.attribute?.id === feature.id ||
        attribute.attribute === feature.id
      );
    });
    if (found_product_features.length === 0) return;
    if (found_product_features.length === 1) {
      sorted_features.push(found_product_features[0]);
      return;
    }
    const feature_facets = feature.facets.map((facet) => facet.value);
    found_product_features.sort(
      (a, b) =>
        feature_facets.indexOf(a.facet) - feature_facets.indexOf(b.facet)
    );

    // Include attributes, sorted
    found_product_features.forEach((product_feature) => {
      sorted_features.push({
        ...product_feature,
        value: getProductFeatureFieldOrValue(product_feature, "value"),
        // Include property for showing the label for
        // a facet rather than the selected value
        facet_display: feature.facets.find(
          ({ value }) => value === product_feature.facet
        )?.display_value
      });
    });
  });
  return {
    ...other_props,
    language:
      maps?.locales?.[language.id] ||
      maps?.languages?.[language.code] ||
      //for scanners
      maps?.locales?.[language],
    [feature_key]: sorted_features.map((feature) => {
      // Re-configure "features" to reference "attributes" and "disclaimers"
      feature.attribute =
        maps[feature_key][feature.attribute?.id || feature.attribute] ||
        maps[feature_key][feature.specification?.id || feature.specification];
      feature.disclaimer = maps.disclaimers[feature.disclaimer?.id];
      //extra transformations to accommodate scanners
      feature.attribute_numeric_value_raw = feature.attribute_numeric_value;
      feature.pivoted_value =
        getProductFeatureFieldOrValue(feature, "value") || "";
      feature.pivoted_field = getProductFeatureFieldOrValue(feature) || "";
      if (!Array.isArray(feature.choices)) {
        feature.choices = JSON.parse(feature.choices);
      }
      // Process numeric_value for when insert_thousands_separator is set to true
      if (feature.attribute?.insert_thousands_separator) {
        const number_with_separators =
          feature.attribute_numeric_value?.toLocaleString?.("en-US");

        // Replace with formatted version, while retaining original value separately
        feature.original_attribute_numeric_value =
          feature.attribute_numeric_value;
        feature.attribute_numeric_value = number_with_separators;
      }
      return feature;
    }),

    // Re-configure "prices" field to reference "price_types"
    prices: prices.map(({ amount, type, currency_display }) => ({
      amount,
      type: maps.price_types[type?.id],
      currency_display
    }))
  };
}

function getProductFeatureFieldOrValue(product_feature, info_key) {
  // if info_key is "value" return field value else return the field name
  const want_value = info_key === "value";
  const { attribute } = product_feature;
  switch (attribute?.input_type) {
    case "text-input":
      return want_value ? product_feature.attribute_value : "attribute_value";
    case "currency-display":
      return want_value ? product_feature.currency_value : "currency_value";
    case "numeric":
      return want_value
        ? product_feature.attribute_numeric_value
        : "attribute_numeric_value";
    case "dropdown":
      return want_value ? product_feature.choice : "choice";
    case "checkboxes":
      return want_value ? JSON.parse(product_feature.choices) : "choices";
    default:
      return want_value ? product_feature.display_value : "display_value";
  }
}

export function localizeFeatureDropdowns(
  find_by_features,
  selectedLocale,
  project_type
) {
  const feature_key = "attributes";
  const selected_locale = selectedLocale.language;
  const local_features = find_by_features.selected_features.map(
    (selected_feature) => {
      let feature_values = [];
      const attribute =
        selected_feature.attribute || selected_feature.specification || {};
      const { groupings = [] } = attribute;

      // Show grouping names
      if (groupings.length > 0) {
        feature_values = groupings.map(({ id, name, label }) => ({
          label: label(selectedLocale.id),
          value: id
        }));
      }
      // Values come from products - scan products list for selected
      // features
      else if (attribute?.values_come_from_products) {
        let unique_values = {};

        // Iterate through products
        find_by_features.selected_products.forEach((selected_product) => {
          // Collect relevant product features
          let relevant_product_features = selected_product
            .getLocalized(feature_key, selected_locale)
            .filter((f) => f.attribute.id === attribute.id);

          relevant_product_features.forEach((feature) => {
            // skip empty values
            if (!feature.display_value) return;

            unique_values[feature.display_value] = true;
          });
        });

        feature_values = Object.keys(unique_values);

        // numeric / text input fields
        switch (attribute?.input_type) {
          case "text-input":
          case "numeric":
            // Convert to expected format for dropdowns: {label, value}
            feature_values = feature_values.map((v) => ({
              label: v,
              value: v
            }));
            break;
          default:
        }
      } else {
        feature_values = attribute.choices;
      }
      return {
        feature_id: attribute?.id,
        groupings: groupings,
        //if nothing is there for label in locales, use default name
        label:
          selected_feature.label ||
          attribute?.getLocalized("label", selected_locale) ||
          attribute?.label,
        values: feature_values.filter((e) => e.label !== "")
      };
    }
  );
  return local_features;
}

export function includeInDropdown(model, comparison_pages) {
  comparison_pages = comparison_pages.filter(
    (page) => page.primary_product !== undefined
  );
  for (const page of comparison_pages) {
    if (page.secondary_products.includes(model)) return true;
  }
  return false;
}

//return list of page components to export
export function pageComponentsToExport(
  pagesToExport,
  productComparePages,
  Component,
  globalPageProps
) {
  let pageList = [];
  const modifiedGlobalPageProps = {
    ...globalPageProps,
    disableComparisonPageExportAndTop: true
  };
  for (const p of pagesToExport) {
    if (p.pages) pageList = pageList.concat(p.pages);
  }
  const pageSet = new Set(pageList);
  pageList = productComparePages.filter((page) => pageSet.has(page));
  pageList.sort((a, b) => {
    //"comparison-110" to 110
    const aId = parseInt(a.id.split("-")[1]);
    const bId = parseInt(b.id.split("-")[1]);
    return aId - bId;
  });
  return pageList.map((pageData) => (
    <Component data={pageData} {...modifiedGlobalPageProps} />
  ));
}

function addPageToProduct(productMap, page, product, brandId) {
  if (product.brand.id !== brandId) return;
  const { model, variant } = product;
  const name = `${model} ${variant}`;
  productMap.set(name, {
    id: product.id,
    pages: new Set([...(productMap.get(name)?.pages || []), page])
  });
}

// return an array of objects in the form {name: productName, pages: pages where product is either primary or secondary product}
export function pagesForProducts(comparisonPages, brandId) {
  //map product name to pages it appears in
  const productMap = new Map();
  const pageQueue = comparisonPages.map((page) => {
    return { page: page, product: page.primary_product, type: "primary" };
  });
  for (const pqObj of pageQueue) {
    const { page, product, type } = pqObj;
    if (type === "primary") {
      //add secondary products to end of queue so all primaries are added first
      for (const secondary of page.secondary_products) {
        pageQueue.push({ page: page, product: secondary, type: "secondary" });
      }
    }
    addPageToProduct(productMap, page, product, brandId);
  }
  //return map as array of objects
  return Array.from(productMap).map(([key, value]) => ({
    name: key,
    id: value.id,
    pages: [...value.pages]
  }));
}

//returns array of all pages to export in array
export function pagesToExportArray(pagesToExport) {
  return (
    pagesToExport
      .flatMap(({ pages }) => pages)
      // Only get unique pages
      .filter((page, index, allPages) => {
        const idxOfOtherPageWithSameId = allPages.findIndex(
          (p) => p.id === page.id
        );
        return idxOfOtherPageWithSameId === index;
      })
  );
}

// Prepares data for a promo for a third layer element (Index, Find by Features, Product Compare)
export const preparePromosData = (promos, is_spe) => {
  let promo_items = [];
  (promos || [])
    // Limit to 3 promos
    .slice(0, 3)
    .forEach((promo) => {
      if (!promo) return;
      promo_items.push({
        heading_disclaimer: promo.heading_disclaimer,
        heading: promo.heading,
        body: promo.body,
        body_disclaimer: promo.body_disclaimer,
        image_url: promo.image_url,
        hide_on_spe: promo.hide_on_spe,
        cta_label: promo.link_text,
        link_class: promo.link_class,
        cta_url: promo.href
      });
    });

  return promo_items;
};

/**
 * Verifies whether a given string could possibly be bundle ID.
 */
export const isIdBundle = (str) => {
  var intVal = parseInt(str);
  if (isNaN(intVal)) return true;
  // Bundle ID may be composed of some integers.
  // We know the string is a bundle ID if the length of the int value
  // is not equivalent to the length of the string itself
  return ("" + intVal).length !== ("" + str).length;
};

export function getQclProductIds(ids) {
  const bundleIdIsProductId = !isIdBundle(ids[0]);
  if (bundleIdIsProductId) return ids.slice(0, 4);
  return ids.slice(1);
}

export function gatherQclSpeData(
  ids,
  projectData,
  selectedLocale,
  selectedAudience
) {
  const compareData = {};
  const [productId0, productId1, productId2, productId3] =
    getQclProductIds(ids);
  var secondary_prod_ids = difference(
    [productId1, productId2, productId3],
    [productId0]
  );
  const product_ids = [productId0, ...secondary_prod_ids];
  const products = product_ids.map(
    (pid) => projectData.collections_maps.products[pid]
  );
  const primary_product = products[0];
  const secondary_products = products.slice(1);
  compareData.primary_product = primary_product;
  compareData.promos = [];
  compareData.secondary_products = secondary_products.filter((p) => !!p);
  compareData.html_id = `comparison-1`;
  compareData.title_formatting = "primary-model";
  compareData.template = "primary-vs-competitors";
  compareData.pricing = {};
  compareData.selected_features =
    projectData.collections.quick_compare_page.enabled_attributes(
      selectedAudience,
      selectedLocale?.language
    );

  compareData.price_types =
    projectData.collections.quick_compare_page.enabled_price_types(
      selectedAudience,
      selectedLocale?.language
    );
  compareData.show_tco_graph = {};
  compareData.gaptco_savings = {};
  let table_config = {
    primary_product: compareData.primary_product,
    secondary_products: compareData.secondary_products,

    attributes: compareData.selected_features,
    price_types: compareData.price_types
  };

  var tableData = collectTableData(
    table_config,
    selectedLocale,
    projectData.collections
  );
  compareData.table = tableData;
  return new ProductCompareData(
    compareData,
    projectData.collections,
    projectData.collections_maps
  );
}

//adds/removes page to selected pages to export
export function pageToDropdown(dropdownList, page, allOption, options) {
  if (page === allOption) return [];
  const dropdownSet = new Set(dropdownList);
  dropdownSet.has(page) ? dropdownSet.delete(page) : dropdownSet.add(page);
  const oneLessItem = dropdownSet.size === options.length - 1;
  !dropdownSet.has(allOption) && oneLessItem
    ? dropdownSet.add(allOption)
    : dropdownSet.delete(allOption);
  return [...dropdownSet];
}
